import { circularMod } from "@faire/deprecated-retailer-visitor-shared/lib/math";
import { setTimeoutIfPossible } from "@faire/next-utils/isomorphic/setTimeoutIfPossible";
import { useViewport } from "@faire/web/common/hooks/useViewport";
import { isPrerender } from "@faire/web/common/user-agent/isPrerender";
import isChromatic from "chromatic/isChromatic";
import { useCallback, useEffect, useRef, useState } from "react";

export const useAnimatedScroll = (tabLength: number, rotationSpeed: number) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const [isVisible, setisVisible] = useState<boolean>(true);
  const { isTabletAndBelow } = useViewport();
  const disableAnimation =
    isPrerender() || isChromatic() || tabLength <= 1 || isTabletAndBelow;

  const fadeInInterval = useRef<number>(0);
  const fadeOutInterval = useRef<number>(0);
  const pauseScroll = useCallback(() => {
    setisVisible(true);
    clearTimeout(fadeOutInterval.current);
    clearTimeout(fadeInInterval.current);
  }, [fadeOutInterval, fadeInInterval]);

  const resumeScroll = useCallback(() => {
    if (disableAnimation) {
      return;
    }
    // Make sure there's always only one set of timeouts
    pauseScroll();
    fadeOutInterval.current =
      setTimeoutIfPossible(() => {
        setisVisible(false);
      }, rotationSpeed) ?? 0;
    fadeInInterval.current =
      setTimeoutIfPossible(() => {
        setCurrentIndex((currentIndex) =>
          circularMod(currentIndex + 1, tabLength)
        );
        setisVisible(true);
        resumeScroll();
      }, rotationSpeed + 500) ?? 0;
  }, [disableAnimation, pauseScroll, rotationSpeed, tabLength]);

  useEffect(() => {
    if (!disableAnimation) {
      resumeScroll();
    }
    return () => {
      pauseScroll(); // cleaning timeouts
    };
  }, [resumeScroll, pauseScroll, disableAnimation]);

  return {
    isVisible,
    resumeScroll,
    pauseScroll,
    currentIndex,
    setCurrentIndex,
  };
};
